import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { RefreshCw, ArrowLeft, AlertTriangle, GitBranch, FileText, Car, Building2, Clock, Hash } from 'lucide-react'
import PipelineVisualization, { AuditStep } from '../../components/PipelineVisualization/PipelineVisualization'
import DataViewer from '../../components/DataViewer/DataViewer'
import styles from './CorrelationAuditDetails.module.scss'
import { config } from '../../config'

const MONITOR_API_URL = config.MONITOR_API_URL

interface CorrelationLogDetail {
  _id: string
  _index: string
  correlation_id: string
  status?: string
  pusher_status?: string
  provider?: string
  dealer_id?: string
  dealer_name?: string
  file_path?: string
  file_extension?: string
  metadata?: {
    date?: string
    provider_id?: string
    filename?: string
    vin?: string
    stock?: string
    make?: string
    model?: string
    year?: number
    file_type?: string
  }
  original_data?: Record<string, unknown>
  transformed_data?: Record<string, unknown>
  audit_history?: AuditStep[]
  pusher_events?: Array<{
    channel?: string
    type?: string
    status?: string
    timestamp?: string
    message?: string
    error?: string
  }>
  published_status?: {
    status?: string
    channel?: string
    queued_at?: string
    processed_at?: string
    worker?: string
    message?: string
    error?: string
  }
  '@timestamp'?: string
  enrichment_timestamp?: string
}

const CorrelationAuditDetails: React.FC = () => {
  const { correlationId } = useParams<{ correlationId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const date = searchParams.get('date')

  const [logDetail, setLogDetail] = useState<CorrelationLogDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchLogDetail = useCallback(async () => {
    if (!correlationId) return

    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams()
      if (date) params.append('date', date)

      const response = await fetch(`${MONITOR_API_URL}/correlation-logs/${encodeURIComponent(correlationId)}?${params}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      const data = await response.json()
      setLogDetail(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch log details')
      setLogDetail(null)
    } finally {
      setLoading(false)
    }
  }, [correlationId, date])

  useEffect(() => {
    fetchLogDetail()
  }, [fetchLogDetail])

  const handleBack = () => {
    navigate('/correlation_audit')
  }

  const getStatusClass = (status: string | undefined): string => {
    if (!status) return styles.statusUnknown
    const s = status.toLowerCase()
    if (s === 'success' || s === 'completed' || s === 'published') return styles.statusSuccess
    if (s === 'failed' || s === 'error') return styles.statusFailed
    if (s === 'pending' || s === 'queued') return styles.statusPending
    return styles.statusUnknown
  }

  const formatTimestamp = (timestamp: string | undefined): string => {
    if (!timestamp) return '-'
    try {
      return new Date(timestamp).toLocaleString()
    } catch {
      return timestamp
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>
          <RefreshCw size={32} className={styles.spinning} />
          <span>Loading correlation log details...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <button className={styles.backBtn} onClick={handleBack}>
            <ArrowLeft size={16} />
            <span>Back to List</span>
          </button>
        </header>
        <div className={styles.errorState}>
          <AlertTriangle size={48} />
          <h3>Error Loading Details</h3>
          <p>{error}</p>
          <button className={styles.retryBtn} onClick={fetchLogDetail}>
            <RefreshCw size={14} />
            <span>Retry</span>
          </button>
        </div>
      </div>
    )
  }

  if (!logDetail) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <button className={styles.backBtn} onClick={handleBack}>
            <ArrowLeft size={16} />
            <span>Back to List</span>
          </button>
        </header>
        <div className={styles.emptyState}>
          <FileText size={48} />
          <h3>Log Not Found</h3>
          <p>The requested correlation log could not be found.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={handleBack}>
            <ArrowLeft size={16} />
            <span>Back</span>
          </button>
          <div className={styles.titleGroup}>
            <div className={styles.logoIcon}>
              <GitBranch size={18} />
            </div>
            <div className={styles.titleInfo}>
              <h1 className={styles.title}>Correlation Details</h1>
              <code className={styles.correlationId}>{logDetail.correlation_id}</code>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <span className={`${styles.statusBadge} ${getStatusClass(logDetail.status)}`}>
            {logDetail.status || 'unknown'}
          </span>
          <button className={styles.refreshBtn} onClick={fetchLogDetail}>
            <RefreshCw size={14} />
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {/* Pipeline Visualization */}
      <section className={styles.section}>
        <PipelineVisualization auditHistory={logDetail.audit_history || []} />
      </section>

      {/* Overview Cards */}
      <section className={styles.overviewGrid}>
        {/* Basic Info Card */}
        <div className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <Hash size={14} />
            <span>Basic Information</span>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Correlation ID</span>
              <code className={styles.infoValue}>{logDetail.correlation_id}</code>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Index</span>
              <span className={styles.infoValue}>{logDetail._index}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Status</span>
              <span className={`${styles.statusBadge} ${getStatusClass(logDetail.status)}`}>
                {logDetail.status || 'unknown'}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Pusher Status</span>
              <span className={`${styles.statusBadge} ${getStatusClass(logDetail.pusher_status)}`}>
                {logDetail.pusher_status || 'N/A'}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Provider</span>
              <span className={styles.infoValue}>{logDetail.provider || '-'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>File Path</span>
              <code className={styles.infoValue}>{logDetail.file_path || '-'}</code>
            </div>
          </div>
        </div>

        {/* Vehicle Info Card */}
        <div className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <Car size={14} />
            <span>Vehicle Information</span>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>VIN</span>
              <code className={styles.infoValue}>{logDetail.metadata?.vin || '-'}</code>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Stock</span>
              <span className={styles.infoValue}>{logDetail.metadata?.stock || '-'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Make</span>
              <span className={styles.infoValue}>{logDetail.metadata?.make || '-'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Model</span>
              <span className={styles.infoValue}>{logDetail.metadata?.model || '-'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Year</span>
              <span className={styles.infoValue}>{logDetail.metadata?.year || '-'}</span>
            </div>
          </div>
        </div>

        {/* Dealer Info Card */}
        <div className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <Building2 size={14} />
            <span>Dealer Information</span>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Dealer ID</span>
              <span className={styles.infoValue}>{logDetail.dealer_id || '-'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Dealer Name</span>
              <span className={styles.infoValue}>{logDetail.dealer_name || '-'}</span>
            </div>
          </div>
        </div>

        {/* Timestamps Card */}
        <div className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <Clock size={14} />
            <span>Timestamps</span>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Created</span>
              <span className={styles.infoValue}>{formatTimestamp(logDetail['@timestamp'])}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Enriched</span>
              <span className={styles.infoValue}>{formatTimestamp(logDetail.enrichment_timestamp)}</span>
            </div>
            {logDetail.published_status?.queued_at && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Queued At</span>
                <span className={styles.infoValue}>{formatTimestamp(logDetail.published_status.queued_at)}</span>
              </div>
            )}
            {logDetail.published_status?.processed_at && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Processed At</span>
                <span className={styles.infoValue}>{formatTimestamp(logDetail.published_status.processed_at)}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Data Sections */}
      <section className={styles.dataSection}>
        {/* Original Data */}
        {logDetail.original_data && Object.keys(logDetail.original_data).length > 0 && (
          <div className={styles.dataCard}>
            <DataViewer data={logDetail.original_data} title="Original Data" defaultView="table" maxHeight={350} />
          </div>
        )}

        {/* Transformed Data */}
        {logDetail.transformed_data && Object.keys(logDetail.transformed_data).length > 0 && (
          <div className={styles.dataCard}>
            <DataViewer
              data={logDetail.transformed_data}
              title="Transformed Data"
              defaultView="table"
              maxHeight={350}
            />
          </div>
        )}

        {/* Metadata */}
        {logDetail.metadata && Object.keys(logDetail.metadata).length > 0 && (
          <div className={styles.dataCard}>
            <DataViewer data={logDetail.metadata} title="Metadata" defaultView="table" maxHeight={250} />
          </div>
        )}

        {/* Pusher Events */}
        {logDetail.pusher_events && logDetail.pusher_events.length > 0 && (
          <div className={styles.dataCard}>
            <DataViewer data={logDetail.pusher_events} title="Pusher Events" defaultView="table" maxHeight={250} />
          </div>
        )}

        {/* Published Status */}
        {logDetail.published_status && Object.keys(logDetail.published_status).length > 0 && (
          <div className={styles.dataCard}>
            <DataViewer
              data={logDetail.published_status}
              title="Published Status"
              defaultView="table"
              maxHeight={200}
            />
          </div>
        )}

        {/* Full Raw Document */}
        <div className={styles.dataCard}>
          <DataViewer data={logDetail} title="Full Document (Raw)" defaultView="json" maxHeight={400} />
        </div>
      </section>
    </div>
  )
}

export default CorrelationAuditDetails
